import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { useSaunaBasePrice } from "../hooks/useSaunaBasePrice";
import ContactModal from "../components/ContactModal";
import UmiTilleggSection from "../components/umi/UmiTilleggSection";
import MoriFaqAccordion from "../components/mori/MoriFaqAccordion";

const GAP = 12; // px between slides

function GallerySlider({ images, label, title, onOpenLightbox }) {
  const [startIdx, setStartIdx] = useState(0);
  const trackRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const visible = isMobile ? 1 : 3;

  const prev = () => setStartIdx((i) => Math.max(0, i - 1));
  const next = () => setStartIdx((i) => Math.min(images.length - visible, i + 1));

  const canPrev = startIdx > 0;
  const canNext = startIdx < images.length - visible;

  const getOffset = () => {
    if (!trackRef.current) return 0;
    const trackWidth = trackRef.current.offsetWidth;
    const slideWidth = (trackWidth - (visible - 1) * GAP) / visible;
    return startIdx * (slideWidth + GAP);
  };

  const [offset, setOffset] = useState(0);
  useEffect(() => {setOffset(getOffset());}, [startIdx, isMobile]);
  useEffect(() => {
    const handleResize = () => setOffset(getOffset());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [startIdx, isMobile]);

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => {touchStartX.current = e.touches[0].clientX;};
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();else
    if (diff < -40) prev();
    touchStartX.current = null;
  };

  return (
    <div className="pt-8 md:pt-12 pb-2">
      <div className="max-w-[1440px] mx-auto px-4 md:px-12 mb-4 flex items-end justify-between">
        <div>
          <p className="text-lg tracking-[0.3em] uppercase text-foreground font-body font-semibold">{label}</p>
          {title && <h2 className="font-heading text-3xl md:text-4xl font-light">{title}</h2>}
        </div>
        <div className="flex gap-2">
          <button onClick={prev} disabled={!canPrev}
          className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${canPrev ? 'border-[#4a8f63] bg-[#4a8f63] text-white hover:bg-[#2d5a3d] hover:border-[#2d5a3d]' : 'border-foreground/10 text-foreground/20 cursor-default'}`}>
            <ChevronLeft size={18} />
          </button>
          <button onClick={next} disabled={!canNext}
          className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${canNext ? 'border-[#4a8f63] bg-[#4a8f63] text-white hover:bg-[#2d5a3d] hover:border-[#2d5a3d]' : 'border-foreground/10 text-foreground/20 cursor-default'}`}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div className="overflow-hidden px-4 md:px-12" ref={trackRef}
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div className="flex transition-transform duration-500 ease-in-out"
        style={{ gap: `${GAP}px`, transform: `translateX(-${offset}px)` }}>
          {images.map((img, i) => {
            const trackWidth = trackRef.current?.offsetWidth || 0;
            const slideWidth = trackWidth ? (trackWidth - (visible - 1) * GAP) / visible : 0;
            return (
              <div key={i} className="flex-shrink-0 cursor-pointer overflow-hidden"
              style={{ width: slideWidth || `calc(${100 / visible}% - ${(visible - 1) * GAP / visible}px)`, height: isMobile ? '60vw' : '480px' }}
              onClick={() => onOpenLightbox(i)}>
                <img src={img} alt={`${label} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>);

          })}
        </div>
      </div>
      {isMobile &&
      <div className="flex justify-center gap-1.5 mt-3">
          {images.map((_, i) =>
        <button key={i} onClick={() => setStartIdx(i)}
        className={`w-1.5 h-1.5 rounded-full transition-all ${i === startIdx ? 'bg-foreground scale-125' : 'bg-foreground/25'}`} />
        )}
        </div>
      }
    </div>);

}

const exteriorImages = [
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/e91981748_ChatGPTImage14mai202615_49_40.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/cb057b3a8_ChatGPTImage14mai202615_51_40.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/7e895ba67_ChatGPTImage14mai202615_53_12.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/182c6fd2f_ChatGPTImage14mai202615_54_41.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/ac3dabfb9_ChatGPTImage14mai202615_57_33.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/db092bcbb_ChatGPTImage14mai202615_58_28.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/d6dcd4879_LYKKE_MORI_1.jpg",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/e7072bef6_LYKKE_MORI_2.jpg",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/457fcea15_LYKKE_MORI_3.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/76f59bdc8_LYKKE_MORI_FULLWALL1.jpg",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/47a74006e_LYKKE_MORI_GRAY1.jpg",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/1bb60fe9d_LYKKE_MORI_BLACK2.jpg"];


const interiorImages = [
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/242fdc638_Huum.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/59ffabf77_INTERIOR_THERMO_ASPEN31.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/61776f81b_INTERIOR_THERMO_ASPEN3.png",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/a2b98cefa_Huum3.jpg",
"https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/7295fe647_IMG_9166.jpg"];


const otherSaunas = [
{ label: "UMI", href: "/sauna-umi", img: "https://media.base44.com/images/public/69a726345560d5ef49a37111/2bea1da1e_LYKKEUMI.jpg" },
{ label: "YORI", href: "/sauna-yori", img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/752b8c1a6_LYKKE_YORI1.jpg" },
{ label: "HIRO", href: "/sauna-hiro", img: "https://media.base44.com/images/public/69a726345560d5ef49a37111/3d5fe43e4_HIRO_1.jpg" },
{ label: "DOLOMITI", href: "/sauna-dolomiti", img: "https://media.base44.com/images/public/69a726345560d5ef49a37111/c109e0135_Dolomiti_Thermo2.jpg" }];


function LightboxGallery({ images, initialIndex, onClose }) {
  const [idx, setIdx] = useState(initialIndex);
  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" onClick={onClose}>
      <button onClick={onClose} className="absolute top-6 right-8 text-white/70 hover:text-white text-3xl z-10">×</button>
      <div className="flex-1 flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={images[idx]}
            alt=""
            className="max-h-[85vh] max-w-[90vw] object-contain"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25 }} />
          
        </AnimatePresence>
        <button onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2"><ChevronLeft size={32} /></button>
        <button onClick={() => setIdx((i) => (i + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2"><ChevronRight size={32} /></button>
      </div>
      <div className="flex gap-2 justify-center py-4 overflow-x-auto px-4">
        {images.map((img, i) =>
        <button key={i} onClick={(e) => {e.stopPropagation();setIdx(i);}} className={`flex-shrink-0 w-14 h-10 overflow-hidden transition-all ${i === idx ? 'ring-2 ring-white' : 'opacity-40 hover:opacity-70'}`}>
            <img src={img} alt="" className="w-full h-full object-cover" />
          </button>
        )}
      </div>
    </div>);

}

export default function SaunaMori() {
  const { basePrice: price } = useSaunaBasePrice('mori', 221000);
  const [lightbox, setLightbox] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const stickyRef = useRef(null);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="bg-[#f5f4f0]">
      <Navbar />

      {/* ── HERO ── */}
      <div className="relative w-full h-[75vh] md:h-[90vh] overflow-hidden">
        <img
          src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/79d22a547_Mori-main.png"
          alt="LYKKE MORI hagebadstue – utendørs badstue med panoramavindu"
          className="absolute inset-0 w-full h-full object-cover" />

        <div className="absolute bottom-0 left-0 px-8 md:px-16 pb-14 md:pb-20">
          <p className="text-white/60 text-xs tracking-[0.3em] uppercase font-body mb-3">KOLEKSJON</p>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[1] mb-3">
            LYKKE MORI
          </h1>
          <p className="font-heading text-3xl md:text-5xl italic text-white/70 font-light leading-tight mb-4">
            Kompakt nordisk badstue
          </p>
        </div>
      </div>

      {/* ── OVERVIEW: sticky left + specs right ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-16 items-start">

          {/* LEFT — sticky overview */}
          <div ref={stickyRef} className="md:sticky md:top-32">
            <p className="text-xs tracking-[0.25em] uppercase text-foreground/50 mb-4 font-body">LYKKE MORI · Kompakt badstue</p>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-6">
              MORI<br />
              <span className="italic opacity-40 text-4xl md:text-5xl">Hagebadstue</span>
            </h2>
            <p className="text-foreground/60 leading-relaxed mb-10 text-2xl max-w-sm">
              Den minste modellen i kolleksjonen for intime hager. Nordisk design med stabil varme for 3–4 personer.
            </p>

            {/* Spec table */}
            <div className="space-y-0 border-t border-foreground/10 mb-10">
              {[
              ["Størrelse", "Plass til 3–4 personer"],
              ["Startpris", `Fra ${price.toLocaleString('nb-NO')} NOK`],
              ["Leveringstid", "6–8 uker"],
              ["Belysning", "LED innvendig og utvendig"],
              ["Innvendig panel", "Or (Asp)"],
              ["Utvendig kledning", "Termofuru, OSMO UV olje"],
              ["Ovn", "HUUM Hive Mini 9 kW"]].
              map(([label, val]) =>
              <div key={label} className="grid grid-cols-2 border-b border-foreground/10 py-2.5 gap-4">
                  <span className="text-2xl text-foreground/40 font-body tracking-wide">{label}</span>
                  <span className="text-2xl text-foreground font-body font-medium">{val}</span>
                </div>
              )}
            </div>

            {/* Plan */}
            <button
              onClick={() => setPlanOpen(true)}
              className="flex items-center gap-2 text-lg font-body text-[#4a8f63] hover:text-[#2d5a3d] transition-colors mb-8 border-b border-[#4a8f63]/30 pb-3 w-full rounded-sm">
              
              <Maximize2 size={15} />
              Se plantegning
            </button>

            {/* CTA */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.href = "/sauna-mori-konfigurator"}
                className="w-full md:w-auto px-10 py-4 bg-foreground text-background text-sm tracking-[0.15em] uppercase font-body hover:bg-foreground/85 transition-all duration-200 rounded-sm">
                
                Konfigurer din MORI
              </button>
              <p className="text-2xl text-foreground/60 font-body">
                Spørsmål?{" "}
                <a href="/be-om-tilbud" className="underline hover:text-foreground transition-colors">Be om tilbud</a>
                {" "}eller{" "}
                <button onClick={() => setContactModalOpen(true)} className="underline hover:text-foreground transition-colors cursor-pointer">kontakt oss</button>.
              </p>
            </div>
          </div>

          {/* RIGHT — specs detail */}
          <div className="space-y-10 pt-2">
            <div className="border-l-2 border-primary/40 pl-6">
              <h3 className="font-heading text-2xl font-light mb-3">Dimensjoner og struktur</h3>
              <div className="space-y-1.5 text-foreground/60 text-2xl">
                <p>Ytre mål: <strong className="text-foreground">359 × 255 × 330 cm</strong></p>
                <p>Grunnflate: <strong className="text-foreground">~9,2 m²</strong></p>
                <p>Veggisolasjon: <strong className="text-foreground">STEICO ~100 mm</strong></p>
                <p>Tak/gulvisolasjon: <strong className="text-foreground">STEICO ~140 mm</strong></p>
                <p>Dampsperre: <strong className="text-foreground">Aluminiumsfolie Paavo</strong></p>
                <p>Vindvern: <strong className="text-foreground">Isola Tyvek Pro Safe</strong></p>
              </div>
            </div>

            <div className="border-l-2 border-primary/40 pl-6">
              <h3 className="font-heading text-2xl font-light mb-3">Interiør</h3>
              <div className="space-y-1.5 text-foreground/60 text-2xl">
                <p>Panel: <strong className="text-foreground">Or STS4 15×120 mm</strong></p>
                <p>Benker: <strong className="text-foreground">Topnivå hengende benker</strong></p>
                <p>Gulv: <strong className="text-foreground">Behandlet tre</strong></p>
                <p>Dør: <strong className="text-foreground">Classic 69×179 cm, herdet glass</strong></p>
                <p>Gratis: <strong className="text-foreground">LYKKE löyly PAKKE (bøtte, øse, termometer, timeglass)</strong></p>
              </div>
            </div>

            <div className="border-l-2 border-primary/40 pl-6">
              <h3 className="font-heading text-2xl font-light mb-3">Ovn og kontroll</h3>
              <div className="space-y-1.5 text-foreground/60 text-2xl">
                <p>Ovn: <strong className="text-foreground">HUUM Hive Mini Sort 9 kW</strong></p>
                <p>Kontroller: <strong className="text-foreground">UKU 4G Black HUUM</strong></p>
                <p>Steiner: <strong className="text-foreground">Komplett sett</strong></p>
                <p>Alternativ: <strong className="text-foreground">HUUM HIVE Wood 13 kW (vedovn)</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── STANDARD EQUIPMENT INTRO ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 md:py-16 border-t border-foreground/10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-foreground/40 font-body mb-3">STANDARDUTSTYR</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6">
              Alle LYKKE MORI-badstuer leveres med et solid standardutstyr.
            </h2>
          </div>
          <div className="space-y-4 text-foreground/60 text-2xl font-body leading-relaxed">
            <p>
              Vi er opptatt av at prisen på badstuene våre skal være så optimalisert som mulig, men samtidig levere maksimal komfort og kvalitet — noe vi anser som en selvfølge.
            </p>
            <p>
              For de som ønsker å tilpasse badstuen til egne behov, gir vi rom for å velge premium-tillegg og personalisering.
            </p>
          </div>
        </div>
      </div>

      {/* ── EXTERIOR GALLERY SLIDER ── */}
      <GallerySlider
        images={exteriorImages}
        label="EKSTERIØR"
        title=""
        onOpenLightbox={(idx) => setLightbox({ images: exteriorImages, idx })} />
      

      {/* ── EXPLORE THE DETAILS ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <p className="text-xs tracking-[0.3em] uppercase text-foreground/40 font-body mb-2">DETALJER</p>
        <h2 className="font-heading text-3xl md:text-5xl font-light mb-12">Utforsk detaljene</h2>
        <div className="grid md:grid-cols-3 gap-10">
          {[
          {
            img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/0a8a58607_Glassogvindu.png",
            title: "Moderne nordisk design",
            text: "Minimalistisk utforming med kraftige linjer og naturlige materialer. Den elegante fasaden kombinerer tradisjonelle elementer med moderne estetikk som passer perfekt i dagens hager."
          },
          {
            img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/f1c62e79b_TERMOFURU.png",
            title: "Kompakt og effektiv",
            text: "Perfekt størrelse for mindre hager og intim opplevelse. Høy isolasjonsstandard sikrer optimal varmeoppfølging og energieffektivitet hele året."
          },
          {
            img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/1b5d36abe_LYKKE_UMI_DETALMORI1.png",
            title: "Naturlig materiale",
            text: "Laget av premium Skandinavisk tre som patinerer vakkert over tid. Hver detalj er håndverket med presisjon for å sikre langtidsholdbarhet og estetisk skjønnhet."
          }].
          map((item) =>
          <div key={item.title}>
              <h3 className="font-body text-2xl font-normal mb-3">{item.title}</h3>
              <p className="text-foreground/60 text-2xl font-body leading-relaxed">{item.text}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── INTERIOR GALLERY SLIDER ── */}
      <GallerySlider
        images={interiorImages}
        label="INTERIØR"
        title=""
        onOpenLightbox={(idx) => setLightbox({ images: interiorImages, idx })} />
      

      {/* ── EXPLORE INTERIOR DETAILS ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <p className="text-xs tracking-[0.3em] uppercase text-foreground/40 font-body mb-2">INTERIØR DETALJER</p>
        <h2 className="font-heading text-3xl md:text-5xl font-light mb-12">Innendørs kvalitet</h2>
        <div className="grid md:grid-cols-3 gap-10">
          {[
          {
            img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/22f462163_INTERIOR_THERMO_ASPEN31.png",
            title: "Innvendig panel",
            text: "Vakker Or-panel (Asp) som gir en lys og rolig atmosfære. Det naturlige treet absorberer fukt optimalt og skaper det autentiske badstuemiljøet med god luftsirkulasjon."
          },
          {
            img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/09b5a09eb_HIRO_INTERIOR_SHOWER2.jpg",
            title: "Ergonomisk interiør",
            text: "Hengende benker maksimerer komforten i den kompakte rommet. Designet sikrer optimal varmefordeling og gir plass til avslapping for hele familien."
          },
          {
            img: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/766c36d79_Gulv.png",
            title: "Holdbar finish",
            text: "Behandlet tregulv motstand fukt og varme. Oppgraderes lett til premiumfliser for ekstra luksus og enkel vedlikehold med årene."
          }].
          map((item) =>
          <div key={item.title}>
              <h3 className="font-body text-2xl font-normal mb-3">{item.title}</h3>
              <p className="text-foreground/60 text-2xl font-body leading-relaxed">{item.text}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── DIVIDER FULL-WIDTH IMAGE SECTION ── */}
      <div className="relative w-full h-[70vh] md:h-[90vh] overflow-hidden">
        <img src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/e852113b4_LYKKE_UMI_DETAL_MORI.png"

        alt="LYKKE MORI"
        className="absolute inset-0 w-full h-full object-cover" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 px-8 md:px-16 pb-14 md:pb-20 max-w-xl">
          <p className="text-white/50 text-xs tracking-[0.3em] uppercase font-body mb-4">TILPASNING</p>
          <h2 className="font-heading text-4xl md:text-6xl font-light text-white leading-[1.1] mb-4">
            Bygg din <em>perfekte</em> badstue
          </h2>
          <p className="text-white/70 text-2xl font-body leading-relaxed">
            Alle LYKKE MORI-badstuer leveres med et solid standardutstyr — men vi vet at detaljer gjør forskjellen. Velg tillegg som passer din stil og dine behov.
          </p>
        </div>
      </div>

      {/* ── CUSTOMIZATION OPTIONS ── */}
      <UmiTilleggSection />

      {/* ── FULL WIDTH MORI IMAGE ── */}
      <div className="relative w-full h-[70vh] md:h-[90vh] overflow-hidden">
        <img src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/c3820747d_LYKKE_MORI_GRAY2.jpg"

        alt="LYKKE MORI"
        className="absolute inset-0 w-full h-full object-cover" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 px-8 md:px-16 pb-14 md:pb-20">
          <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[1] mb-3">
            LYKKE MORI
          </h2>
          <p className="font-heading text-2xl md:text-4xl italic text-white/70 font-light">
            Kompakt nordisk badstue
          </p>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <p className="text-xs tracking-[0.3em] uppercase text-foreground/40 font-body mb-2 text-center">FAQ</p>
        <h2 className="font-heading text-3xl md:text-5xl font-light mb-12 text-center">Vanlige spørsmål</h2>
        <MoriFaqAccordion />
      </div>

      {/* ── ORDER CTA ── */}
      <div className="bg-[#f0ede6] py-24 md:py-32">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-foreground/40 font-body mb-6">NESTE STEG</p>
          <h2 className="font-heading text-4xl md:text-6xl font-light leading-[1.2] mb-6">
            Klar til å ta det neste steget?
          </h2>
          <p className="text-foreground/60 text-2xl leading-relaxed mb-4 max-w-xl mx-auto">
            Trenger du mer informasjon? Ta en titt på vår{" "}
            <a href="/laer-mer-handverk" className="underline text-[#4a8f63] hover:text-[#2d5a3d] transition-colors tracking-widest uppercase text-xs font-body">LÆR MER</a>
            {" "}der vi detaljert beskriver materialene vi bruker, prosessen og alt du trenger.
          </p>
          <p className="text-foreground/60 text-2xl leading-relaxed mb-12 max-w-xl mx-auto">
            Har du allerede all informasjonen du trenger — velkommen til å konfigurere din badstue, eller ta kontakt med oss direkte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = "/sauna-mori-konfigurator"}
              className="px-10 py-4 bg-foreground text-background text-sm tracking-[0.15em] uppercase font-body hover:bg-foreground/85 transition-all duration-200 rounded-sm">
              
              Konfigurer
            </button>
            <button
              onClick={() => window.location.href = "/laer-mer-handverk"}
              className="px-10 py-4 border border-[#4a8f63] text-[#4a8f63] text-sm tracking-[0.15em] uppercase font-body hover:bg-[#4a8f63] hover:text-background transition-all duration-200 rounded-sm">
              
              Lær mer
            </button>
            <button
              onClick={() => setContactModalOpen(true)}
              className="px-10 py-4 border border-foreground/30 text-foreground text-sm tracking-[0.15em] uppercase font-body hover:bg-foreground hover:text-background transition-all duration-200 rounded-sm">
              
              Kontakt oss
            </button>
          </div>
        </div>
      </div>

      {/* ── BROWSE RANGE ── */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24">
        <h2 className="font-heading text-3xl font-light mb-10">Andre modeller</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {otherSaunas.map((sauna) =>
          <a
            key={sauna.label}
            href={sauna.href}
            className="group block overflow-hidden">
            
              <div className="aspect-[3/4] overflow-hidden mb-3 rounded-sm">
                <img
                src={sauna.img}
                alt={sauna.label}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              
              </div>
              <div className="flex items-center justify-between">
                <span className="font-heading text-xl font-light">{sauna.label}</span>
                <span className="text-sm text-foreground/40 group-hover:text-foreground transition-colors tracking-widest uppercase font-body">Se →</span>
              </div>
            </a>
          )}
        </div>
      </div>

      <Footer />

      {/* ── LIGHTBOX ── */}
      {lightbox &&
      <LightboxGallery
        images={lightbox.images}
        initialIndex={lightbox.idx}
        onClose={() => setLightbox(null)} />

      }

      {/* ── PLAN MODAL ── */}
      {planOpen &&
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setPlanOpen(false)}>
          <div className="relative max-w-2xl w-full bg-background p-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPlanOpen(false)} className="absolute top-3 right-4 text-foreground/50 hover:text-foreground text-2xl">×</button>
            <img
            src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/24ea953ae_rysunek_transparent.png"
            alt="Plantegning MORI"
            className="w-full h-auto" />
          
          </div>
        </div>
      }

      <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </motion.div>);

}